import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import { IconButton, ListItem, ListItemIcon, ListItemText, Menu, TextField } from '@material-ui/core';
import { Check, ChevronRight, Close, LanguageOutlined, SearchOutlined } from '@material-ui/icons';

import { joinClassNames } from '../../util/util';
import { convertToTranscriptSegment, TranscriptSegment } from '../../util/media-data-util';
import { setShowTranscriptionsModule, setSubtitleLanguage } from '../../actions/configurationActions';
import { useListCustomStyle } from '../Controls/SettingsMenu/MenuUtils';

import "./TranscriptionContainer.scss";

interface TranscriptionContainerProps {
    settings?: any;
    isMobile?: boolean;
    queryParams?: any
}
const baseClass = "transcription-container";
export const TranscriptionContainer = ({ settings, isMobile }: TranscriptionContainerProps) => {
    const dispatch = useDispatch();
    const { t: translator } = useTranslation();

    const { currentMediaId, playlistId, subtitleLanguage, transcriptions } = useSelector((state) => (state as any).configurationReducer);
    const { currentPosition } = useSelector((state) => (state as any).playerReducer);

    const color = useMemo(() => settings?.playerStyling?.primaryColor ?? '#126cfc', [settings?.playerStyling?.primaryColor]);
    const classes = useListCustomStyle();

    const transcriptionContainerRef = useRef<HTMLDivElement | null>(null);
    const transcriptionContainerContentRef = useRef<HTMLDivElement | null>(null);
    const searchButtonRef = useRef<HTMLButtonElement | null>(null);
    const highlightedTextRef = useRef<string[]>([])
    const contentTouchStartRef = useRef<Boolean>(false)
    const languageSelectorAnchor = useRef<HTMLButtonElement | null>(null);
    const [openLanguageSelector, setOpenLanguageSelector] = React.useState(false);
    const [openSearchField, setOpenSearchField] = React.useState(false);
    const [selectedLanguage, setSelectedLanguage] = React.useState(transcriptions?.language || "en");
    const [searchText, setSearchText] = React.useState("");
    const [focusingHighlightedTextIndex, setFocusingHighlightedTextIndex] = React.useState(0);
    const [focusingItemId, setFocusingItemId] = React.useState("");
    const [wheelScroll, setWheelScroll] = React.useState(false);

    const transcriptionData = useMemo(() => {
        if ((transcriptions?.entries || []).length === 0) {
            return [];
        }

        let entries = transcriptions?.entries || [];
        if (selectedLanguage !== transcriptions?.language) {
            entries = transcriptions.translations?.[selectedLanguage as any] || [];
        }

        let transcriptSegments = convertToTranscriptSegment(entries);
        transcriptSegments = transcriptSegments.map((item: TranscriptSegment, index: number) => {
            const active = item.start <= currentPosition * 1000 && item.end >= currentPosition * 1000;
            item.active = active;
            item.id = `transcription-segment-${index}`
            return item;
        });

        return transcriptSegments;

    }, [selectedLanguage, transcriptions, currentPosition]);

    const transcriptLanguages = useMemo(() =>{
        if (!transcriptions?.language) {
            return [];
        }
        return transcriptions ? [transcriptions?.language, ...(transcriptions?.translationLanguages ?? [])].filter(Boolean) : []
    }, [transcriptions]);

    const changeTranscriptLanguage = useCallback((lang: string) => {
        setSelectedLanguage(lang);
        setOpenLanguageSelector(false);
        dispatch(setSubtitleLanguage({ code: lang }));
    }, [dispatch]);

    const onSegmentClick = useCallback((item: TranscriptSegment) => {
		const widget = (window as any).GoBrain.widgets()[`player-${playlistId || currentMediaId}`];
        widget && widget.position(item.start/1000);
        setWheelScroll(false);
        contentTouchStartRef.current = false;
    }, [currentMediaId, playlistId]);

    const onOpenSearchField = useCallback(() => {
        setOpenSearchField(true);
        setTimeout(() => {
            const input = document.querySelector(`.${baseClass}__search input`) as HTMLInputElement;
            input && input.focus();
        }, 0);
    }, []);

    const onJumpToSearch = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            setFocusingHighlightedTextIndex(prev => prev + 1);
        }
        if (e.key === "ArrowDown") {
            transcriptionContainerContentRef.current?.focus();
        }
    }, []);

    const closeTranscriptionModule = useCallback(() => {
        dispatch(setShowTranscriptionsModule(false));
        setOpenSearchField(false);
        setSearchText("");
        setFocusingHighlightedTextIndex(0);
        highlightedTextRef.current = [];

        const bottomControlbarLanguageMenu = document.querySelector('.bottomControlbarLanguageMenu') as any;
        bottomControlbarLanguageMenu && bottomControlbarLanguageMenu.focus();
    }, [dispatch]);

    const searchInputAriaLabel = useMemo(() => {
        if (!searchText || isNaN(focusingHighlightedTextIndex % highlightedTextRef.current?.length)) {
            return "";

        }
        return `${translator("TRANSCRIPTION_MODULE_SEARCH_RESULT")} ${(focusingHighlightedTextIndex % highlightedTextRef.current?.length)+1}${translator("TRANSCRIPTION_MODULE_SEARCH_RESULT_OUT_OF")}${highlightedTextRef.current.length}`.trim()
    }, [searchText, focusingHighlightedTextIndex, translator]);

    const transcriptionContentAriaLabel = useMemo(() => {
        if (focusingItemId) {
            const focusingItem = transcriptionData.find((item: TranscriptSegment) => item.id === focusingItemId);
            return `${focusingItem?.text}. ${translator("TRANSCRIPTION_MODULE_CONTENT")}`;
        }
        return "";
    }, [focusingItemId, transcriptionData, translator]);

    const scrollTextToCenter = useCallback((queryString: string) => {
            const elemToScroll = document.querySelector(queryString) as any;
            if (elemToScroll && !wheelScroll) {
                elemToScroll.scrollIntoView({ behavior: "smooth", block: "center" });
            }
    }, [wheelScroll]);

    const handleKeyDownOnTranscriptionContent = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            setFocusingItemId("");
            setWheelScroll(false);
            contentTouchStartRef.current = false;
        }

        if (event.key?.includes('Arrow')) {
            event.preventDefault();
            const transcriptionDataToFocus = searchText ? transcriptionData.filter((item: TranscriptSegment) => item.text.toLowerCase().includes(searchText.trim().toLowerCase())) : transcriptionData;
            const currentFocusingItemIndex = focusingItemId ? transcriptionDataToFocus.findIndex((item: TranscriptSegment) => item.id === focusingItemId) : -1;

            const delta = event.key === 'ArrowUp' || event.key === "ArrowLeft" ? -1 : 1;
            const nextItemIndex = (currentFocusingItemIndex + delta + transcriptionDataToFocus.length) % transcriptionDataToFocus.length;
            const nextItem = transcriptionDataToFocus[nextItemIndex];
            setFocusingItemId(nextItem?.id);
        }
        if (event.key === 'Enter' || event.key === ' ') {
            const item = transcriptionData.find((item: TranscriptSegment) => item.id === focusingItemId);
            if (item) {
                onSegmentClick(item);
            }
        }
    }, [focusingItemId, onSegmentClick, searchText, transcriptionData]);

    useEffect(() => {
        const markElements = document.querySelectorAll(`mark`);
        highlightedTextRef.current = Array.from(markElements).map((item: any) => item.id);
        if (searchText.trim() && highlightedTextRef.current.length > 0) {
            const highlightedTextId = highlightedTextRef.current[focusingHighlightedTextIndex % highlightedTextRef.current.length];
            scrollTextToCenter(`#${highlightedTextId}`);
        }
    }, [focusingHighlightedTextIndex, scrollTextToCenter, searchText]);

    useEffect(() => {
        if (focusingItemId) {
            scrollTextToCenter(`.${baseClass}__text--focusing`);
        }
    }, [focusingItemId, scrollTextToCenter]);

    useEffect(() => {
        if (transcriptionData.some((item: TranscriptSegment) => item.active) && !focusingItemId) {
            scrollTextToCenter(`.${baseClass}__text--active`);
        }
    }, [focusingItemId, scrollTextToCenter, transcriptionData]);

    useEffect(() => {
        if (subtitleLanguage?.code === selectedLanguage || subtitleLanguage.code?.includes(selectedLanguage)) {
            return;
        }
        const transcriptCorrespondingLang = transcriptLanguages.find((lang: string) => lang === subtitleLanguage?.code)
                                        ?? transcriptLanguages.find((lang: string) => subtitleLanguage?.code?.includes(lang));
        transcriptCorrespondingLang && setSelectedLanguage(transcriptCorrespondingLang);
    }, [transcriptLanguages, subtitleLanguage?.code, selectedLanguage]);

    useEffect(() => {
        requestAnimationFrame(() => {
            transcriptionContainerRef?.current?.focus();
        })
    }, [])

    return <div
        key={"transcription-module"}
        className={joinClassNames(baseClass, isMobile ? `${baseClass}--mobile` : ``)}
        onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeTranscriptionModule();
            }
        }}
    >
        <div
            className={`${baseClass}__header`}
            ref={r => transcriptionContainerRef.current = r}
            tabIndex={0}
            aria-label={translator("TRANSCRIPTION_MODULE_TITLE")}
        >
            <IconButton disabled={openSearchField} onClick={closeTranscriptionModule} title={translator("TRANSCRIPTION_MODULE_CLOSE_BUTTON")}>
                {isMobile ? <Close htmlColor='#000000'/> : <ChevronRight htmlColor='#000000'/>}
            </IconButton>
            <div className={`${baseClass}__title`}><span aria-hidden="true">Transcription</span></div>
            <IconButton
                disabled={openSearchField}
                title={translator("TRANSCRIPTION_MODULE_LANGUAGE_MENU")}
                ref={r => languageSelectorAnchor.current = r}
                onClick={() => setOpenLanguageSelector(!openLanguageSelector)}
                aria-haspopup="true"
            >
                {<LanguageOutlined htmlColor='#000000'/>}
            </IconButton>

            <TextField
                disabled={!openSearchField}
                className={joinClassNames(`${baseClass}__search`, openSearchField ? `${baseClass}__search--expanded` : "")}
                variant="outlined"
                size="small"
                placeholder={translator("TRANSCRIPTION_MODULE_SEARCH_BUTTON")}
                value={searchText}
                onChange={(e) => {
                    const sanitizedText = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
                    setSearchText(sanitizedText);
                }}
                onKeyUp={onJumpToSearch}
                InputProps={{
                    startAdornment: <IconButton ref={r => searchButtonRef.current = r} onClick={onOpenSearchField} disabled={openSearchField} title={translator("TRANSCRIPTION_MODULE_SEARCH_BUTTON")}>
                        <SearchOutlined htmlColor='#000000'/>
                    </IconButton>,
                    endAdornment: openSearchField ? <>
                        <span role="status" aria-atomic="true" className={`${baseClass}__helper-text`} aria-label={searchInputAriaLabel}>
                            <span aria-hidden="true">{searchText && highlightedTextRef.current.length > 0 ? `${(focusingHighlightedTextIndex % highlightedTextRef.current.length)+1}/${highlightedTextRef.current.length}` : ""}</span>
                        </span>
                        <IconButton
                            title={translator("TRANSCRIPTION_MODULE_CLOSE_SEARCH_BUTTON")}
                            onClick={() => {
                                setSearchText("");
                                setOpenSearchField(false);
                                setTimeout(() => searchButtonRef.current?.focus(), 0);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Tab' && !event.shiftKey) {
                                    setSearchText("");
                                    setOpenSearchField(false);
                                }
                            }}
                            disabled={!openSearchField}
                        >
                            <Close htmlColor='#000000'/>
                        </IconButton>
                    </> : undefined
                }}
            />
        </div>

        <div
            tabIndex={0}
            className={`${baseClass}__content`}
            aria-label={translator("TRANSCRIPTION_MODULE_CONTENT")}
            ref={r => transcriptionContainerContentRef.current = r}
            onFocus={() => setFocusingItemId("")}
            onBlur={() => setFocusingItemId("")}
            onKeyDown={handleKeyDownOnTranscriptionContent}
            onWheel={() => setWheelScroll(true)}
            onTouchStart={() => contentTouchStartRef.current = true}
            onScroll={() => contentTouchStartRef.current && setWheelScroll(true)}
        >
            {transcriptionData.map((item: TranscriptSegment, index) => {
                return <Highlighter
                    id={item.id}
                    key={index}
                    className={joinClassNames(`${baseClass}__text`, item.active ? `${baseClass}__text--active` : "", focusingItemId === item.id ? `${baseClass}__text--focusing` : "")}
                    onClick={() => onSegmentClick(item)}
                    textToHighlight={item.text}
                    searchWords={searchText.trim() ? [searchText.trim()] : []}
                    highlightTag={({ children, highlightIndex }) => {
                        const id = `mark-${index}-${highlightIndex}`;
                        const highlightedTextId = highlightedTextRef.current[focusingHighlightedTextIndex % highlightedTextRef.current.length];
                        return (
                            <mark
                                id={id}
                                className={joinClassNames("highlighted-text", highlightedTextId === id ? `${baseClass}__text--active` : "" )}
                                onMouseDown={() => onSegmentClick(item)}
                            >
                                {children}
                            </mark>
                          )
                    }}
                />
            })}
        </div>

        <div
            style={{ height: "0", width: "0", overflow: "hidden" }}
            role="region"
            aria-live="assertive"
            aria-atomic="true"
        >
            {transcriptionContentAriaLabel}
        </div>

        <Menu
            onMouseDown={(e) => e.stopPropagation()}
            key={'transcript-menu'}
            open={openLanguageSelector}
            onClose={() => setOpenLanguageSelector(false)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            anchorEl={languageSelectorAnchor.current}
			PaperProps={{
				className: 'closeable-by-tab',
			}}
            onKeyUp={(event) => {
                if (event.key !== 'Tab') {
                    return;
                }
				const isCloseableByTabMenu = !!(event.target as any).classList.contains('closeable-by-tab');
				if (isCloseableByTabMenu) {
					setOpenLanguageSelector(false);
				}
            }}
        >
            {transcriptLanguages.map((lang: string, index: number) => {
                return <ListItem
                    key={index}
                    button
                    onClick={() => changeTranscriptLanguage(lang)}
                    onKeyDown={(event) => {
                        if (event.key === 'Tab') {
                            event.stopPropagation();
                            return;
                        }
                        if (event.key === 'Enter' || event.key === ' ') {
                            changeTranscriptLanguage(lang)
                            return;
                        }
                    }}
                >
                    <ListItemText primary={translator(lang?.toUpperCase())}/>
                    <ListItemIcon style={{ minWidth: 24, marginLeft: 16 }}>
                        {lang === selectedLanguage ? <Check className={classes.listItemIcon} htmlColor={color}/> : <div style={{ width: 24 }} />}
                    </ListItemIcon>
                </ListItem>;
            })}
        </Menu>
    </div>;
}